/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { useContext, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { RegistrationContext } from './context/RegistrationContext'
import { Main } from './steps'
import { RegistrationSteps } from '@sportninja/common/types'
import EmptyRegistration from './EmptyRegistration'
import LoadingRegistration from './LoadingRegistration'
import RegistrationNotOpen from './RegistrationNotOpen'
import { getSelectedForm } from '../RegistrationPreview/utils'
import { restoreFormAnswers } from '../../utils/restoreFormAnswers'
import { useRegistrationPeriod } from '@sportninja/common/hooks/useRegistrationPeriod'
import {
  slugForServerStep,
  slugForStep,
  registrationStepPath,
  registrationSlugPath,
  registrationBasePath,
} from './registrationSteps'
import { stripServerTrace } from '@sportninja/common/utils/stripServerTrace'
import { startRoleFor } from '@sportninja/common/features/registration/startStep/startRole'
import { sessionTeamToRestore } from './utils/sessionTeamRestore'
import { isUserValidated } from './utils/userValidated'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { REGISTRATION_TEAM_PARAM } from '@sportninja/common/features/registration/registrationUrlParams'
import { SESSION_LINK_CONFLICT_COPY } from '@sportninja/common/features/registration/teamLink/constants'
import { sessionLinkConflict } from '@sportninja/common/features/registration/teamLink/sessionLinkConflict'
import WarningModal from 'src/components/WarningModal'
import { useSessionLinkConflict } from './hooks/useSessionLinkConflict'
import { withoutSelectionParams } from './utils/selectionParams'
export const REGISTRATION_STEPS = {
  SELECT_YOUR_DIVISION: 0,
  SIGN_UP: 1,
  LOGIN: 2,
  TEAM_REGISTER: 3,
  REGISTRATION_PAYMENT: 4,
  CONFIRM_YOUR_EMAIL: 5,
  REGISTER_REVIEW: 6,
  SELECT_YOUR_ROLE: 7,
  WAIVER: 8,
}

const RegistrationInvite = ({
  login,
  logout,
  isLogged,
  genericSportProperties,
}) => {
  const { id } = useParams()
  const history = useHistory()
  const {
    session,
    setSession,
    registration,
    setZipCode,
    setCountry,
    setBirthDate,
    setDivision,
    setCouponInfo,
    setAdditionalTeamOfficials,
    userId,
    setPromoCode,
    setCredentials,
    playerType,
    teamOfficialType,
    role,
    setRole,
    validateCoupon,
    setSelectedPlayer,
    selectedTeam,
    setSelectedTeam,
    players,
    userTeams,
    types,
    setCity,
    setEmergencyEmail,
    setOtherMembershipName,
    setOtherMembershipNumber,
    // Player Info
    setFirstName,
    setLastName,
    setPassword,

    // Address
    setAddress,
    setStateProvince,

    // Parents/Guardian Info
    setParentFirstName,
    setParentLastName,
    setParentEmail,
    setParentPhone,

    // Emergency Contact Info
    setEmergencyFirstName,
    setEmergencyLastName,
    setEmergencyPhone,
    setParentRelationship,
    setDoctorName,
    setDoctorPhone,
    setAllergies,
    setMedicalConditions,

    // Preferences
    setPreferredPosition,
    setJerseyNumber,
    setDominantSide,
    setJerseySize,
    setTshirtSize,
    setShortSize,
    setHockeyUSANumber,
    setHockeyCanadaNumber,
    setNotes,
    error,
    setError,
    loading,
    setLoading,
    setId,
    fetchCountries,
    countries,
    refetchPlayers,
    refetchTeamOfficials,
    fetchTypes,
    typesError,
    countriesError,
    fetchUserInfo,
    userInfo,
    setAnswers,
    resetSession,
  } = useContext(RegistrationContext)

  // A stored session that a `?team_id=` link conflicts with. While one is held
  // here it is NOT applied — see `fetchPreviousSession` — so nothing routes,
  // and the start step cannot auto-advance, until the reader has chosen.
  const {
    heldSession: linkConflictSession,
    holdSession: holdLinkConflictSession,
    cancel: cancelLinkConflict,
    startOver: startOverFromLink,
    isStartingOver,
  } = useSessionLinkConflict({ resetSession })

  const isRegistrationLive = registration?.registration?.is_live

  const sessionIdFromUrl =
    new URLSearchParams(window.location.search).get(
      'registration_session_uid'
    ) ||
    new URLSearchParams(window.location.search).get('registration_session_id')

  // Set by "New Registration" / "Start Over" when opening a fresh registration in
  // a new tab. `registration_session_<id>` lives in localStorage, which every tab
  // on this origin shares, so without this the new tab would rehydrate and resume
  // the very session the user just chose to leave.
  const forceNewSession =
    new URLSearchParams(window.location.search).get('nreg') === '1'

  // 🔄 useRegistrationPeriod: Determine if registration is open based on user's local timezone
  const { status: registrationStatus, message: registrationPeriodMessage } =
    useRegistrationPeriod(
      registration?.registration?.starts_at,
      registration?.registration?.ends_at
    )

  useEffect(() => {
    if (!isLogged) {
      return
    }
    fetchUserInfo()
  }, [isLogged])

  useEffect(() => {
    if (typesError) {
      setError(typesError)
      return
    }
    if (!types || types === null) {
      setError(null)
      fetchTypes()
    }
  }, [fetchTypes, types, typesError])

  useEffect(() => {
    if (countriesError) {
      setError(countriesError)
      return
    }
    if (countries.length === 0) {
      setError(null)
      fetchCountries()
    }
  }, [countries.length, countriesError])

  // Effects
  useEffect(() => {
    if (userId) {
      refetchPlayers()
    }
  }, [refetchPlayers, userId])

  useEffect(() => {
    if (userId) {
      refetchTeamOfficials()
    }
  }, [refetchTeamOfficials, userId])

  useEffect(() => {
    setId(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  /**
   * Fill an EMPTY team selection from the session — develop's rule, unchanged
   * in kind: it never replaces a team the context is holding.
   *
   * Forward navigation does not rely on this. The start step writes its commit
   * straight into the context on Next (`committedSelection`), and a rep's pick on
   * the register step's dropdown stays theirs until submitted. What remains is
   * the blank case: a reload, or a rep reaching the register step with nothing
   * held whose earlier team the session still records.
   *
   * The one extension is the source, by role: a player's team is resolved from
   * the registration payload (they chose it on the start step, and `userTeams`
   * is no longer fetched for players); a rep's comes from their own teams only.
   * See `sessionTeamToRestore` for why each.
   *
   * There is deliberately no division counterpart. The start step hands its
   * division over on Next as well, and a reload is covered by `setSessionData`
   * below, as in develop.
   */
  useEffect(() => {
    const team = sessionTeamToRestore({
      sessionTeamId: session?.team_id,
      hasSelection: Boolean(selectedTeam),
      role: startRoleFor(role, playerType, teamOfficialType),
      userTeams,
      registrationTeams: registration?.teams,
    })
    if (team) {
      setSelectedTeam(team)
    }
  }, [
    session,
    selectedTeam,
    role,
    playerType,
    teamOfficialType,
    userTeams,
    registration?.teams,
  ])

  useEffect(() => {
    const createSession = async () => {
      try {
        setError(null)
        setLoading(true)
        const { data } = await req(`/registration/${id}/session/`, {
          method: 'POST',
        })
        localStorage.setItem(
          `registration_session_${id}`,
          JSON.stringify(data?.id)
        )
        setSession(data)
        // A brand-new session starts at the first step. Reconcile the URL to a
        // valid slug so Main (which renders purely from the URL) shows content —
        // the bare /registration/:id route has no :step and would render blank.
        history.replace(
          registrationStepPath(
            id,
            REGISTRATION_STEPS.SELECT_YOUR_DIVISION,
            history.location.search
          )
        )
      } catch (e) {
        // Surface the API's own message, minus the PHP file/line suffix it
        // appends (e.g. "... in /var/www/html/.../Foo.php:57"), which is noise
        // to the visitor and leaks server paths.
        const apiMessage = stripServerTrace(e?.message)
        setError(
          new Error(apiMessage || `Registration not found for id: ${id}`)
        )
      } finally {
        setLoading(false)
      }
    }
    const setSessionData = async (data) => {
      // Reconcile the URL to the server's step. The API is the source of truth;
      // Main renders from whatever slug the URL ends up on.
      //
      // The `select_division` server step is a two-way branch: a validated
      // logged-in user who already picked a division resumes on the start step;
      // anyone else goes to confirm-email (the email-confirmation gate), NOT
      // back to a bare division list. All other server steps map directly via
      // slugForServerStep.
      //
      // The forward target is the MERGED start step, not the old select-role
      // screen. Role and division are one question now, so resuming on step 7
      // would drop the reader onto a screen the flow no longer uses — and one
      // that cannot show the division they already chose.
      let targetSlug = slugForServerStep(data?.registration_step)
      if (data?.registration_step === RegistrationSteps.SELECT_YOUR_DIVISION) {
        // `userInfo` arrives in two shapes — see `isUserValidated`. Reading only
        // the API's snake_case key sent every Redux-sourced reader to the email
        // gate, which then bounced them onward a moment later.
        targetSlug =
          data?.division_id && userId && isUserValidated(userInfo)
            ? slugForStep(REGISTRATION_STEPS.SELECT_YOUR_DIVISION)
            : slugForStep(REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL)
      }
      history.replace(
        registrationSlugPath(id, targetSlug, history.location.search)
      )
      if (data?.player_id) {
        setSelectedPlayer(players?.find((p) => p?.id === data?.player_id))
      }
      if (String(data?.registration_type_id) === String(playerType?.id)) {
        setRole(playerType)
      } else if (
        String(data?.registration_type_id) === String(teamOfficialType?.id)
      ) {
        setRole(teamOfficialType)
      }
      if (data?.coupon_code) {
        setCouponInfo(data?.coupon_code)
        setPromoCode(data?.coupon_code?.code)
        await validateCoupon(
          data?.coupon_code?.code,
          data?.division_id,
          data?.registration_type_id
        )
      }
      if (data?.postal_code) {
        setZipCode(data?.postal_code)
      }
      if (data?.country_code) {
        setCountry(data?.country_code)
      }
      if (data?.division_id) {
        const selectedDivision = registration?.divisions?.find((d) => {
          return d?.schedule_id === data?.division_id
        })
        setDivision(selectedDivision)
      }
      // We should set the additional team officials if they exist
      // the first one is the main team official, the logged in user
      if (data?.team_official_array && data?.team_official_array.length > 1) {
        const teamOfficials = data?.team_official_array
          .map((official) => {
            return {
              email: official?.email,
              firstName: official?.name_first,
              lastName: official?.name_last,
              teamOfficialTypeId: official?.team_official_type_id,
              isTreasurer: official?.is_treasurer,
              phone: official?.phone,
            }
          })
          .slice(1)
        setAdditionalTeamOfficials(teamOfficials)
      }
      if (data?.player_array && data?.player_array.length > 0) {
        const player = data?.player_array[0]
        setFirstName(player?.name_first)
        setLastName(player?.name_last)
        setPassword(player?.password)

        setAddress(player?.address)
        setZipCode(player?.postal_code)
        setStateProvince(player?.state_province)
        setCountry(player?.country)
        setBirthDate(player?.birth_date?.split('T')[0] || null)

        setParentFirstName(player?.parent_first_name)
        setParentLastName(player?.parent_last_name)
        setParentEmail(player?.parent_email)
        setParentPhone(player?.parent_phone_number)

        setEmergencyFirstName(player?.emergency_first_name)
        setEmergencyLastName(player?.emergency_last_name)
        setEmergencyPhone(player?.emergency_phone_number)
        setParentRelationship(player?.emergency_relationship)
        setDoctorName(player?.doctor_name)
        setDoctorPhone(player?.doctor_phone_number)
        setAllergies(player?.known_allergies)
        setMedicalConditions(player?.medical_conditions)

        setPreferredPosition(player?.player_type_id)
        setJerseyNumber(player?.preferred_jersey_number)
        setDominantSide(player?.preferred_dominant_side)
        setJerseySize(player?.preferred_jersey_size)
        setTshirtSize(player?.preferred_tshirt_size)
        setShortSize(player?.preferred_short_size)
        setHockeyUSANumber(player?.hockey_usa_number)
        setHockeyCanadaNumber(player?.hockey_canada_number)
        setNotes(player?.additional_comments_notes)

        setCity(player?.city)
        setEmergencyEmail(player?.emergency_email)
        setOtherMembershipName(player?.custom_membership_name)
        setOtherMembershipNumber(player?.custom_membership_number)
      }

      /**
       * 🔄 DynamicForm restoration (v2 only)
       *
       * Restores form answers from persisted session data for registration v2.
       * This allows users to resume their registration with previously entered
       * dynamic form data intact.
       *
       * @description Only runs when:
       * - registration_version === 2 (new dynamic registration)
       * - session contains form data (data.form exists)
       * - form definition is available for the current role
       *
       * @see restoreFormAnswers - Utility that performs the actual restoration
       * @since Registration v2 - Dynamic form session restoration
       */
      try {
        const isV2 =
          registration?.registration?.root_schedule?.registration_version === 2
        if (isV2 && data?.form) {
          console.log(
            '🔄 setSessionData: Attempting to restore DynamicForm answers',
            {
              hasFormData: !!data.form,
              formSections: Object.keys(data.form || {}),
              registrationVersion:
                registration?.registration?.root_schedule?.registration_version,
            }
          )

          // Determine form type based on role
          const currentRole =
            String(data?.registration_type_id) === String(teamOfficialType?.id)
              ? teamOfficialType
              : playerType
          const formType =
            currentRole?.id === teamOfficialType?.id ? 'team' : 'player'

          // Get the form definition for the current role
          const formDef = getSelectedForm(registration, formType)

          if (formDef) {
            console.log(
              '🔄 setSessionData: Form definition found, restoring answers',
              {
                formId: formDef.id,
                formType,
                groupCount: formDef.groups?.length || 0,
              }
            )

            // Restore the form answers from session data
            const restored = restoreFormAnswers(formDef, data.form)

            // Overwrite all answers atomically to avoid merging stale state
            setAnswers(() => restored)

            console.log(
              '🔄 setSessionData: DynamicForm answers restored successfully',
              {
                restoredFieldCount: Object.keys(restored).length,
                restoredFields: Object.keys(restored),
              }
            )
          } else {
            console.log(
              '🔄 setSessionData: No form definition found for role',
              {
                formType,
                roleId: currentRole?.id,
                roleName: currentRole?.name,
              }
            )
          }
        } else {
          console.log('🔄 setSessionData: Skipping DynamicForm restoration', {
            isV2,
            hasFormData: !!data?.form,
            reason: !isV2 ? 'Not registration v2' : 'No form data in session',
          })
        }
      } catch (restoreErr) {
        console.error(
          '❌ setSessionData: Failed to restore DynamicForm answers',
          {
            error: restoreErr,
            sessionData: data,
            registrationVersion:
              registration?.registration?.root_schedule?.registration_version,
          }
        )
        // Don't throw - restoration failure shouldn't break the session loading
      }
    }
    const fetchPreviousSession = async (sessionId) => {
      try {
        setError(null)
        setLoading(true)
        const { data } = await req(`/registration/session/${sessionId}`)
        if (
          data?.root_schedule_id !==
          registration?.registration?.root_schedule_id
        ) {
          localStorage.removeItem(`registration_session_${id}`)
          createSession()
          return
        }
        // A `?team_id=` link naming a different team than a session with
        // progress: ask before applying either (`sessionLinkConflict`).
        // Credentials are applied as they would be anyway — they belong to the
        // reader, not to the session being decided on.
        // A link the session has already acted on is consumed instead.
        const urlTeamId = new URLSearchParams(history.location.search).get(
          REGISTRATION_TEAM_PARAM
        )
        // The role id is passed so a team registration in progress is always
        // prompted about — a `team_id` link is a player registration.
        const linkConflict = sessionLinkConflict({
          urlTeamId,
          session: data,
          teamOfficialTypeId: teamOfficialType?.id,
        })
        if (linkConflict === 'prompt') {
          if (data?.bearer_token && data?.user) {
            setCredentials(data?.bearer_token, data?.user)
          }
          holdLinkConflictSession(data)
          // Fetched but deliberately NOT applied: the prompt holds it while the
          // reader decides, so nothing below may route off it.
          return
        }
        if (linkConflict === 'consume') {
          // The session already holds the linked team past the start step:
          // the link has been acted on. Drop it before routing, so it neither
          // rides along to the step the session resumes on nor sends Back
          // into an auto-advance.
          history.replace({
            search: withoutSelectionParams(history.location.search),
          })
        }
        setSession(data)
        setSessionData(data)
        if (data?.bearer_token && data?.user) {
          setCredentials(data?.bearer_token, data?.user)
        }
      } catch (e) {
        localStorage.removeItem(`registration_session_${id}`)
        /*
         * Deep-linked into a step URL but the session is gone (new tab / expired): strip
         * the SLUG so we restart cleanly at the base route, keeping the deep-link params.
         *
         * `registrationBasePath` is the right tool again now that the team link is an
         * ordinary deep-link param: `team_id` is on its allow-list, so a visitor whose
         * session expired still lands back on their team.
         */
        history.replace(registrationBasePath(id, window.location.search))
        createSession()
      } finally {
        setLoading(false)
      }
    }
    // Outside the registration window the API rejects session creation with a
    // 422, which would surface as an error screen on top of the countdown or
    // closed screen. Only reach for a session once registration is actually open.
    const canOpenSession = isRegistrationLive || registrationStatus === 'active'

    // A logged-in reader's resume route reads `userInfo` (see `isUserValidated`),
    // and this whole effect is gated on `!session` — so it decides the route
    // ONCE. Running before `fetchUserInfo` resolves would settle on the email
    // gate with nothing to read, and nothing would revisit it. Wait for the
    // value rather than reading it early.
    const isUserReady = !isLogged || (players !== null && userInfo !== null)

    // `!linkConflictSession`: nothing is fetched or created while a conflicting
    // session is held either — the reader's answer to the prompt decides what
    // happens next. Folded in here so it gates every branch below at once.
    const canStartSession =
      registration &&
      !session &&
      !linkConflictSession &&
      types &&
      canOpenSession &&
      isUserReady

    if (sessionIdFromUrl && canStartSession) {
      fetchPreviousSession(sessionIdFromUrl)
    } else if (canStartSession) {
      const previousSession = forceNewSession
        ? null
        : localStorage.getItem(`registration_session_${id}`)
      if (!previousSession) {
        createSession()
      } else {
        fetchPreviousSession(previousSession.split('"').join(''))
      }
    }
  }, [
    id,
    registration,
    session,
    userId,
    // Both gate the fetch above: the resume route is decided once, from
    // `userInfo`, so the effect must not run before that value exists.
    userInfo,
    isLogged,
    sessionIdFromUrl,
    forceNewSession,
    types,
    players,
    playerType,
    teamOfficialType,
    isRegistrationLive,
    registrationStatus,
    // Clearing it (Cancel) re-runs the resume, now without the link.
    linkConflictSession,
  ])

  return (
    <div
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100%;
        overflow-y: scroll;
        /* Adjustment for the Background issue */
        background-color: ${colors.NEUTRAL_900};
      `}
    >
      {/* Also while a conflicting session is held, so the prompt sits over the
          same loading screen the reader was already looking at. */}
      {(loading || linkConflictSession) && !error ? (
        <LoadingRegistration />
      ) : null}
      {error ? <EmptyRegistration errorMessage={error?.message} /> : null}
      {!isRegistrationLive && registrationStatus === 'before' ? (
        <RegistrationNotOpen
          startsAt={registration?.registration?.starts_at}
          name={registration?.registration?.name}
        />
      ) : null}
      {!isRegistrationLive && registrationStatus === 'after' ? (
        <EmptyRegistration
          title='Registration is closed'
          errorMessage={registrationPeriodMessage}
        />
      ) : null}
      {!loading &&
      !error &&
      (isRegistrationLive || registrationStatus === 'active') &&
      session ? (
        <Main />
      ) : null}
      {/* Closing it with the X declines the link, the same as CANCEL. */}
      <WarningModal
        isOpen={Boolean(linkConflictSession)}
        onClose={cancelLinkConflict}
        onConfirm={startOverFromLink}
        title={capitalizeWords(SESSION_LINK_CONFLICT_COPY.title)}
        description={SESSION_LINK_CONFLICT_COPY.description}
        cancelLabel={SESSION_LINK_CONFLICT_COPY.cancelLabel}
        confirmLabel={SESSION_LINK_CONFLICT_COPY.confirmLabel}
        isLoading={isStartingOver}
      />
    </div>
  )
}

export default RegistrationInvite
